import React from 'react'
import PropTypes from 'prop-types'

// @material-ui
import { Fab, Tooltip } from '@material-ui/core'
import { Close, DeleteForever } from '@material-ui/icons'

// core
import { useGlobal } from '_core/hooks/useGlobal'
import { withLang } from '_core/hocs/withLang'
import { hasProfilePhoto, getProfilePhotoSrc } from '_core/utils/profilePhoto'
import { FileUpload } from '_core/components/FileUpload'
import { Loading } from '_core/components/Loading'
import { Avatar } from '_core/components/Avatar'

// style
import { useAvatarUploadStyle } from './avatarUploadStyle'

export const RawAvatarUpload = props => {
  const {
    value,
    userInfo,
    resetText,
    clearText,
    uploadText,
    onReset,
    onClear,
    getLangString: l,
    ...rest
  } = props

  const classes = useAvatarUploadStyle()

  const {
    config: { VISITOR_PHOTO_DEFAULT },
  } = useGlobal()

  const hasUserPhoto =
    hasProfilePhoto(userInfo) && value !== VISITOR_PHOTO_DEFAULT.id

  const letterProps = {
    login: userInfo.login,
    firstname: userInfo.firstname,
    lastname: userInfo.lastname,
  }

  const renderFiles = ({ files, onClick, onRemoveFile }) => {
    let name
    let imageProps = {}
    let hasNewAvatar = false

    if (files != null) {
      name = Object.keys(files)[0]
      hasNewAvatar = files[name].result != null
    }

    if (files == null && hasUserPhoto) {
      imageProps.alt = userInfo.login
      imageProps.src = getProfilePhotoSrc(userInfo, 'md')
    } else if (hasNewAvatar) {
      imageProps.alt = userInfo.login
      imageProps.src = files[name].preview
    }

    return (
      <div className={classes.root}>
        <Avatar
          size="lg"
          onClick={onClick}
          tooltip={l(uploadText)}
          letterProps={letterProps}
          className={classes.avatar}
          {...imageProps}
        />

        {hasNewAvatar ? (
          <Tooltip title={l(resetText)} placement="right">
            <Fab
              size="small"
              color="secondary"
              className={classes.fab}
              onClick={async () => {
                await onRemoveFile(name)
                onReset()
              }}
            >
              <Close />
            </Fab>
          </Tooltip>
        ) : null}

        {files == null && hasUserPhoto ? (
          <Tooltip title={l(clearText)} placement="right">
            <Fab
              size="small"
              color="secondary"
              className={classes.fab}
              onClick={onClear}
            >
              <DeleteForever />
            </Fab>
          </Tooltip>
        ) : null}

        {files != null && !hasNewAvatar ? (
          <Loading
            className={classes.progress}
            progressProps={{ size: '100%', color: 'secondary' }}
          />
        ) : null}
      </div>
    )
  }

  return (
    <FileUpload
      {...rest}
      title={uploadText}
      renderFiles={renderFiles}
      filesGridProps={{ className: classes.uploadFilesGrid }}
      buttonGridProps={{ className: classes.uploadButtonGrid }}
      classes={{
        root: classes.uploadRoot,
        helperText: classes.uploadHelperText,
      }}
    />
  )
}

RawAvatarUpload.defaultProps = {
  multiple: false,
  accept: 'image/*',
  resetText: 'r._.avatarupload.action.reset',
  clearText: 'r._.avatarupload.action.clear',
  uploadText: 'r._.avatarupload.action.upload',
}

RawAvatarUpload.propTypes = {
  // self props
  value: PropTypes.number,
  userInfo: PropTypes.object,
  resetText: PropTypes.string,
  clearText: PropTypes.string,
  uploadText: PropTypes.string,
  onClear: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,

  // `withLang` HOC props
  langInfo: PropTypes.object,
  getLangObject: PropTypes.func,
  getFirstLangString: PropTypes.func,
  getLangStringSet: PropTypes.func,
  getLangString: PropTypes.func.isRequired,
}

export const AvatarUpload = withLang(RawAvatarUpload)
